import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth-store'
import { PageLoader } from '@/components/ui/page-loader'
import { Pagination } from '@/components/ui/pagination'
import { TruncationNotice } from '@/components/ui/truncation-notice'
import { invoicesService } from '@/services/invoices-service'

const PAGE_SIZE = 25
import { SendInvoiceModal } from '@/components/invoice/send-invoice-modal'
import { RecordPaymentModal } from '@/components/invoice/record-payment-modal'
import type { InvoiceRecord } from '@/types'

type InvoiceStatus = 'draft' | 'sent' | 'viewed' | 'partially_paid' | 'paid' | 'overdue' | 'void'
type TabKey = 'all' | 'draft' | 'sent' | 'overdue' | 'paid' | 'void' | 'scheduled'

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
}

const TAB_CONFIG: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All Invoices' },
  { key: 'draft', label: 'Draft' },
  { key: 'sent', label: 'Sent' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'paid', label: 'Paid' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'void', label: 'Void' },
]

interface SummaryData {
  totalOutstanding: number
  overdueCount?: number
  overdue30?: number
  overdue60?: number
  overdue90?: number
  paidThisMonth: number
  sentThisMonth: number
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function InvoicesListPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabKey>('all')
  const [search, setSearch] = useState('')
  const [invoices, setInvoices] = useState<InvoiceRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [take, setTake] = useState(1000)
  const [loadingAll, setLoadingAll] = useState(false)
  const [summary, setSummary] = useState<SummaryData>({
    totalOutstanding: 0,
    overdueCount: 0,
    paidThisMonth: 0,
    sentThisMonth: 0,
  })

  // Row action menu state
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Modal state for row actions
  const [sendModalInvoice, setSendModalInvoice] = useState<InvoiceRecord | null>(null)
  const [paymentModalInvoice, setPaymentModalInvoice] = useState<InvoiceRecord | null>(null)

  const user = useAuthStore((s) => s.user)
  const userRole = user?.role || ''
  const isManagerOrAbove = userRole === 'MANAGER' || userRole === 'ADMIN' || userRole === 'SUPERADMIN'

  // Close row menu on outside click
  useEffect(() => {
    if (!menuOpenId) return
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpenId])

  // Fetch invoices from API
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const result = await invoicesService.list({ take })
        if (!cancelled) {
          setInvoices(result.invoices || [])
          setTotal(result.total ?? (result.invoices?.length || 0))
        }
      } catch (err) {
        console.error('Failed to load invoices:', err)
        if (!cancelled) setLoadError('Failed to load invoices. Please refresh the page.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  async function loadInvoices(takeArg = take) {
    const result = await invoicesService.list({ take: takeArg })
    setInvoices(result.invoices || [])
    setTotal(result.total ?? (result.invoices?.length || 0))
  }

  async function handleShowAll() {
    if (loadingAll || total <= take) return
    setLoadingAll(true)
    try {
      await loadInvoices(total)
      setTake(total) // only after success — a failure leaves the button usable
    } catch (err) {
      console.error('Failed to load all invoices:', err)
    } finally {
      setLoadingAll(false)
    }
  }

  // Fetch summary data
  useEffect(() => {
    let cancelled = false
    async function loadSummary() {
      try {
        const result = await invoicesService.getSummary()
        if (!cancelled) setSummary(result)
      } catch (err) {
        console.error('Failed to load summary:', err)
      }
    }
    loadSummary()
    return () => { cancelled = true }
  }, [])

  // Map to resolved invoice data with role-based filtering
  const resolved = invoices.map((inv) => ({
    record: inv,
    clientName: inv.clientName || '',
    projectName: inv.title || '',
  }))

  // Scheduled = draft stage invoices with no invoice number yet
  const isScheduled = (inv: InvoiceRecord) => inv.invoiceNumber === null && !!inv.stageNumber

  // Filter by tab
  const filteredByTab = resolved.filter((inv) => {
    if (activeTab === 'scheduled') return isScheduled(inv.record)
    // All other tabs hide scheduled invoices (they have no number to display)
    if (isScheduled(inv.record)) return false
    if (activeTab === 'all') return true
    return inv.record.status === activeTab
  })

  // Filter by search (client-side)
  const filtered = filteredByTab.filter((inv) => {
    if (!search) return true
    const q = search.toLowerCase()
    const invoiceNumber = inv.record.invoiceNumber?.toLowerCase() || ''
    const client = inv.clientName.toLowerCase()
    const project = inv.projectName.toLowerCase()
    return invoiceNumber.includes(q) || client.includes(q) || project.includes(q)
  })

  // Role-based visibility
  const visible = isManagerOrAbove
    ? filtered
    : filtered.filter((inv) => inv.record.createdByUserId === user?.id)

  const [page, setPage] = useState(1)
  const totalPages = Math.ceil(visible.length / PAGE_SIZE)
  const pagedVisible = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // Void invoice inline
  async function handleVoidInvoice(e: React.MouseEvent, record: InvoiceRecord) {
    e.stopPropagation()
    setMenuOpenId(null)
    if (!confirm('Are you sure you want to void this invoice?')) return
    try {
      const updated = await invoicesService.voidInvoice(record.id, 'Voided by user')
      setInvoices((prev) => prev.map((inv) => inv.id === updated.id ? updated : inv))
    } catch (err) {
      console.error('Failed to void invoice:', err)
      alert('Failed to void invoice. Please try again.')
    }
  }

  // Download PDF
  async function handleDownloadPdf(e: React.MouseEvent, record: InvoiceRecord) {
    e.stopPropagation()
    setMenuOpenId(null)
    try {
      const blob = await invoicesService.downloadPdf(record.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${record.invoiceNumber || 'invoice'}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download PDF:', err)
      alert('Failed to download PDF. Please try again.')
    }
  }

  if (loading) {
    return (
      <div>
        <div className="est-list-head">
          <h1 className="est-list-title">Invoices</h1>
        </div>
        <PageLoader />
      </div>
    )
  }

  if (loadError) {
    return (
      <div>
        <div className="est-list-head">
          <h1 className="est-list-title">Invoices</h1>
        </div>
        <div style={{ padding: '48px', textAlign: 'center' }}>
          <p style={{ fontWeight: 500, color: 'var(--red)' }}>{loadError}</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Page Header */}
      <div className="est-list-head">
        <h1 className="est-list-title">Invoices</h1>
        <div className="search-wrap">
          <input
            type="text"
            className="search-input"
            placeholder="Search by invoice #, client, or project..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          />
          <span className="search-icon">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="inv-summary">
        <div className="inv-summary-card">
          <p className="inv-summary-label">Total Outstanding</p>
          <p className="inv-summary-value">{formatCurrency(summary.totalOutstanding)}</p>
        </div>
        <div className="inv-summary-card">
          <p className="inv-summary-label">Overdue (30+ days)</p>
          <p className="inv-summary-value inv-summary-overdue">{formatCurrency(summary.overdue30 || 0)}</p>
        </div>
        <div className="inv-summary-card">
          <p className="inv-summary-label">Paid This Month</p>
          <p className="inv-summary-value inv-summary-paid">{formatCurrency(summary.paidThisMonth)}</p>
        </div>
        <div className="inv-summary-card">
          <p className="inv-summary-label">Sent This Month</p>
          <p className="inv-summary-value">{summary.sentThisMonth}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="est-list-tabs">
        {TAB_CONFIG.map((tab) => (
          <button
            key={tab.key}
            className={`est-list-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab.key); setPage(1) }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <TruncationNotice shown={invoices.length} total={total} onShowAll={handleShowAll} loading={loadingAll} />

      {/* Invoices Table */}
      <div className="card" style={{ marginTop: '15px', overflow: 'visible' }}>
        {pagedVisible.length === 0 && visible.length === 0 ? (
          <div className="inv-empty">
            <svg style={{ width: '48px', height: '48px', margin: '0 auto 12px', color: 'var(--s300)' }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1-2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" />
            </svg>
            <p style={{ fontWeight: 500, color: 'var(--s500)' }}>No invoices found</p>
            <p style={{ fontSize: '13px', color: 'var(--s400)', marginTop: '4px' }}>
              {search ? 'Try a different search term.' : 'Invoices will appear here.'}
            </p>
          </div>
        ) : (
          <>
          <table className="tbl">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Client</th>
                <th>Project</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>Balance Due</th>
                <th>Status</th>
                <th style={{ width: '120px' }}></th>
              </tr>
            </thead>
            <tbody>
              {pagedVisible.map((inv) => (
                <tr
                  key={inv.record.id}
                  className="inv-row"
                  onClick={() => navigate(`/invoices/${inv.record.id}`)}
                >
                  <td>
                    <span className="inv-id">
                      {inv.record.invoiceNumber ?? <span style={{ color: 'var(--s400)', fontStyle: 'italic' }}>Scheduled</span>}
                    </span>
                  </td>
                  <td className="inv-client-name">{inv.clientName}</td>
                  <td>{inv.projectName}</td>
                  <td>{formatCurrency(inv.record.total || 0)}</td>
                  <td>{formatDate(inv.record.dueDate)}</td>
                  <td>{formatCurrency(inv.record.amountDue || 0)}</td>
                  <td>
                    <span className={`inv-status ${inv.record.status}`}>
                      {STATUS_LABELS[inv.record.status as InvoiceStatus] || inv.record.status}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', position: 'relative' }}>
                    <button
                      className="inv-actions-btn"
                      onClick={(e) => {
                        e.stopPropagation()
                        setMenuOpenId(menuOpenId === inv.record.id ? null : inv.record.id)
                      }}
                    >
                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <circle cx="8" cy="3" r="1.5" />
                        <circle cx="8" cy="8" r="1.5" />
                        <circle cx="8" cy="13" r="1.5" />
                      </svg>
                    </button>
                    {menuOpenId === inv.record.id && (
                      <div className="inv-actions-menu" ref={menuRef}>
                        <button
                          className="inv-actions-menu-item"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/invoices/${inv.record.id}`)
                            setMenuOpenId(null)
                          }}
                        >
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          View
                        </button>
                        {(inv.record.status === 'draft' || inv.record.status === 'sent') && inv.record.invoiceNumber && (
                          <button
                            className="inv-actions-menu-item"
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuOpenId(null)
                              setSendModalInvoice(inv.record)
                            }}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" />
                            </svg>
                            {inv.record.status === 'sent' ? 'Resend' : 'Send'}
                          </button>
                        )}
                        {(inv.record.status !== 'paid' && inv.record.status !== 'void' && inv.record.invoiceNumber) && (
                          <button
                            className="inv-actions-menu-item"
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuOpenId(null)
                              setPaymentModalInvoice(inv.record)
                            }}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                            Record Payment
                          </button>
                        )}
                        <button
                          className="inv-actions-menu-item"
                          onClick={(e) => handleDownloadPdf(e, inv.record)}
                        >
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                          Download PDF
                        </button>
                        {(inv.record.status !== 'paid' && inv.record.status !== 'void') && (
                          <button
                            className="inv-actions-menu-item inv-actions-menu-danger"
                            onClick={(e) => handleVoidInvoice(e, inv.record)}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                            Void
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={visible.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
          </>
        )}
      </div>

      {/* Send modal (from row menu) */}
      {sendModalInvoice && (
        <SendInvoiceModal
          invoiceId={sendModalInvoice.id}
          invoiceNumber={sendModalInvoice.invoiceNumber}
          clientEmail={sendModalInvoice.clientEmail}
          open={!!sendModalInvoice}
          onClose={() => setSendModalInvoice(null)}
          onSent={async () => {
            setSendModalInvoice(null)
            await loadInvoices()
          }}
        />
      )}

      {/* Record Payment modal (from row menu) */}
      {paymentModalInvoice && (
        <RecordPaymentModal
          invoiceId={paymentModalInvoice.id}
          invoiceNumber={paymentModalInvoice.invoiceNumber}
          amountDue={paymentModalInvoice.amountDue}
          open={!!paymentModalInvoice}
          onClose={() => setPaymentModalInvoice(null)}
          onRecorded={async () => {
            setPaymentModalInvoice(null)
            await loadInvoices()
          }}
        />
      )}
    </div>
  )
}
